<template>
  <div class="outer">
    <div class="row editor">
      <div class="editor__image">
        <div class="editor__image--inner">
          <vue-cropper
            v-if="imgSrc"
            class="editor__image--cropper"
            :viewMode="2"
            :autoCrop="false"
            :autoCropArea="0.9"
            :checkCrossOrigin="true"
            :zoomable="false"
            crossOrigin="anonymous"
            ref="cropper"
            :src="imgSrc"
            alt="Source Image"
            :cropmove="cropImage"
          />
        </div>
      </div>
      <div class="editor__controls">
        <q-btn
          @click="cropperMode === 'crop' ? update() : setCropperMode('crop')"
          :color="cropperMode === 'crop' ? 'primary' : 'ctrl-turquoise'"
          :icon="cropperMode === 'crop' ? 'check' : 'crop'"
        />
      </div>
      <div class="row info">
        <q-input
          color="primary"
          class="q-ml-md q-mr-md"
          :readonly="true"
          v-model="image.width"
          :float-label="getString('module.library.details.file.image.width')"
        />
        <q-input
          color="primary"
          class="q-ml-md q-mr-md"
          :readonly="true"
          v-model="image.height"
          :float-label="getString('module.library.details.file.image.height')"
        />
        <div class="info__save q-pt-md">
          <q-btn
            class="capitalize ctrl-button q-ml-md"
            color="warning"
            :label="getString('module.library.button.editor.cancel')"
            @click="cancelEdit()"
          />
          <q-btn
            class="capitalize ctrl-button right q-ml-md"
            :color="!cropperMode ? 'ctrl-turquoise' : 'grey-5'"
            :disable="!!cropperMode"
            :label="getString('module.library.button.editor.save')"
            @click="save()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueCropper from 'vue-cropperjs';
import _debounce from 'lodash.debounce';
import qs from 'qs';

import getString from '@/helpers/GetStrings';
import common from '@/mixins/library/common';

export default {
  mixins: [common],
  components: {
    VueCropper,
  },
  data() {
    return {
      cropped: false,
      previewSave: false,
      imgSrc: null,
      coordinates: [],
      image: {
        width: 0,
        height: 0,
      },
      cropperData: {},
      setFocus: false,
      history: {
        index: 0,
        operations: [],
      },
      flip: {
        x: 1,
        y: 1,
      },
      cropperMode: null,
      cropImage: null,
      cropBoxData: null,
      imageData: null,
      cropData: {},
    };
  },
  methods: {
    cropStart() {
      this.cropperMode = 'crop';
      if (this.$refs.cropper !== undefined) {
        this.$refs.cropper.setCropBoxData(this.cropperData);
        this.$refs.cropper.setDragMode('crop');
      }
    },
    getDetail() {
      this.axios.get(`spareparts/pdf/${this.$route.params.id}`).then((response) => {
        this.data = JSON.parse(JSON.stringify(response.data.spare_part_pdf));
        this.category = this.data.spare_parts.filter(
          category => category.id === 1 * this.$route.params.categoryId,
        )[0];
        if (this.category && this.category.items && this.category.items.length) {
          this.mapCoordinates(this.category.items);
        }
        this.imgSrc = this.buildFileLocation(this.category.ev_public_path);
        this.image.height = this.category.picture_height;
        this.image.width = this.category.picture_width;
      });
    },

    getString,

    cancelEdit() {
      this.$router.go(-1);
    },

    mapCoordinates(items) {
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.coordinates && item.coordinates.length) {
          for (let j = 0; j < item.coordinates.length; j += 1) {
            const coord = item.coordinates[j];
            this.coordinates.push({
              xMin: coord.xMin * 1,
              xMax: coord.xMax * 1,
              yMin: coord.yMin * 1,
              yMax: coord.yMax * 1,
              id: coord.id,
              item_id: item.id,
            });
          }
        }
      }
    },

    setCropperMode(mode) {
      this.setFocus = false;
      this.removeFocusStyle();
      this.$refs.cropper.clear();
      if (this.cropperMode === mode) {
        this.cropperMode = null;
        return;
      }
      this.$refs.cropper.initCrop();
      this.cropperMode = mode;
      this.$refs.cropper.setCropBoxData(this.cropperData);
      this.$refs.cropper.setDragMode(mode);
    },

    update() {
      this.cropData = this.$refs.cropper.getData();
      const filetype = this.determineFileType(this.imgSrc);
      const temp = this.$refs.cropper.getCroppedCanvas();
      temp.setAttribute('crossOrigin', 'anonymous');
      const newImg = temp.toDataURL(filetype);

      this.imgSrc = newImg;
      this.$refs.cropper.replace(newImg);
      this.updateImageData();
      this.cropperMode = null;
      this.cropped = true;
      this.updateCoordinates();
    },

    updateCoordinates() {
      for (let i = 0; i < this.coordinates.length; i += 1) {
        const coord = this.coordinates[i];
        coord.xMin -= this.cropData.x;
        coord.xMax -= this.cropData.x;
        coord.yMin -= this.cropData.y;
        coord.yMax -= this.cropData.y;
      }
    },

    // Reads a file and returns the filetype. eg. 'image/jpeg'
    determineFileType(file) {
      let filetype;
      const ext = file
        .split('.')
        .pop()
        .split('/')[0];

      switch (ext) {
        case 'jpeg':
        case 'jpg':
          filetype = 'image/jpeg';
          break;
        case 'png':
          filetype = 'image/png';
          break;
        case 'svg':
          filetype = 'image/svg+xml';
          break;
        case 'tiff':
        case 'tif':
          filetype = 'image/tiff';
          break;
        case 'gif':
          filetype = 'image/gif';
          break;
        case 'bmp':
          filetype = 'image/bpm';
          break;
        case 'ico':
          filetype = 'image/vnd.microsoft.icon';
          break;
        default:
          filetype = '';
          break;
      }
      return filetype;
    },
    async save() {
      const filetype = this.determineFileType(this.imgSrc);
      // Store a base64 string in img variable.
      const img = this.$refs.cropper.getCroppedCanvas().toDataURL(filetype);
      let toBlob;

      await fetch(img)
        .then(res => res.blob())
        .then((blob) => {
          toBlob = blob;
        });

      toBlob.name = 'exploded-view.png';
      // return;
      await this.axios.put(
        `/spareparts/image/details/${this.$route.params.categoryId}`,
        qs.stringify({
          x_offset: this.category.x_offset + this.cropData.x,
          y_offset: this.category.y_offset + this.cropData.y,
          picture_width: this.image.width,
          picture_height: this.image.height,
        }),
      );

      this.axios
        .put(`/spareparts/image/file/${this.$route.params.categoryId}`, toBlob, {
          headers: { 'Content-Disposition': 'inline' },
        })
        .then(() => {
          this.$q.notify({
            message: 'Succefully saved the image',
            type: 'positive',
            timeout: 1200,
            position: 'top-right',
          });
          setTimeout(() => {
            this.$router.push(`/spare-parts/${this.$route.params.id}`);
          }, 500);
        })
        .catch((err) => {
          this.$q.notify({
            message: 'Unable to save the image',
            type: 'warning',
            timeout: 1200,
            position: 'top-right',
          });
          console.warn(err);
        });
    },

    buildFileLocation(location) {
      return location && `${process.env.VUE_APP_API_URL}${location}`;
    },

    _base64ToArrayBuffer(base64) {
      const binaryString = window.atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      return bytes.buffer;
    },

    removeFocusStyle() {
      this.$refs.cropper.clear();
      const elements = document.querySelectorAll('[ref=focus]');
      for (let i = 0; i < elements.length; i += 1) {
        elements[i].setAttribute('ref', 'crop');
      }
      setTimeout(() => {
        // Re-initialize the cropper
        this.$refs.cropper.initCrop();
      }, 10);
    },

    initFocus() {
      if (this.cropperMode === 'focus') {
        this.$refs.cropper.clear();
        this.cropperMode = null;
      } else {
        this.cropperMode = 'focus';
      }
    },

    updateImageData: _debounce(function (initialLoad) {
      // Do not add initialization to history
      if (!initialLoad) {
        this.history.operations.push('cropmove');
      }
      if (this.$refs.cropper) {
        this.cropperData = this.$refs.cropper.getCroppedCanvas();
        this.image.width = this.cropperData.width.toFixed(0);
        this.image.height = this.cropperData.height.toFixed(0);
      }
    }, 100),

    cropmove() {
      this.updateImageData();
    },

    addToHistory(operation) {
      this.history.operations.push(operation);
      this.history.index += 1;
    },

    applyOperation(dir) {
      const operation = Object.keys(this.history.operations[this.history.index])[0];
      const value = Object.values(this.history.operations[this.history.index])[0];

      // Appplies undo/redo operations
      switch (operation) {
        case 'flipX':
          this.flip.x = dir * -value;
          this.flipImgage('x', true);
          break;
        case 'flipY':
          this.flip.y = dir * -value;
          this.flipImgage('y', true);
          break;
        case 'rotate':
          this.rotateImage(dir * value, true);
          break;
        default:
          break;
      }
    },
  },

  computed: {
    canSave() {
      // Determines whether saving is possible
      // There should be changes and there should be a crop-box initialized (not in focus mode)
      return this.history.operations.length > 0 && !this.setFocus;
    },
  },

  created() {
    window.addEventListener('cropmove', this.cropmove, true);
    window.addEventListener('cropstart', this.cropStart, true);
  },
  destroyed() {
    window.removeEventListener('cropmove', this.cropmove, true);
    window.removeEventListener('cropstart', this.cropmove, true);
  },
  mounted() {
    this.getDetail();

    if (this.$refs.cropper) {
      this.updateImageData(true);
      this.$refs.cropper.clear();
    }
  },
};
</script>
